import { ReactNode, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useGetOneIngredient } from "src/api/client-api/ingredients/useGetOneIngredient";
import { useGetIngredientPosts } from "src/api/client-api/post/useGetIngredientPosts";
import { useGetOneIngredientReferenceUnified } from "src/api/client-api/ingredientReferenceUnified/useGetOneIngredientReferenceUnified";
import { buildCompositeId } from "src/api/client-api/ingredientReferenceUnified/types";
import { INGREDIENT, useFoodpilotContext, VIEW_SSQ } from "src/context/FoodpilotContext";
import { useGetOneSsqVersion } from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { ScoreService } from "src/services/ScoreService";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { useOlapScoresParams } from "src/context/OlapScores/useOlapScoresParams";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { useGetIngredientPost } from "src/api/client-api/post/useGetIngredientPost";
import { useGetIngredientScores } from "src/api/client-api/olapScores/useGetIngredientScores";
import { IngredientContext, IngredientContextInitialValues } from "./useIngredientContext";
import { IngredientLoadStateBoundary } from "./IngredientLoadStateBoundary";
import {
    getAnswerableSupplierId,
    getQuestionnaireSupplierId,
    getReadableSupplierIds,
    selectReadableSupplierCompanies,
} from "../ingredientQuestionnaireAccess";

type IngredientContextProviderProps = { children: ReactNode };
export const IngredientContextProvider = (props: IngredientContextProviderProps) => {
    const { children } = props;
    const { ingredientId, supplierId } = useParams();
    const { isUserOfTypeSupplier } = useFoodpilotContext();
    const [activeCompanyId, setActiveCompanyId] = useState<number | null>(null);

    const { ...ingredientsData } = useAllIngredientData({
        ingredientId,
        activeCompanyId,
    });

    const showIngredientSuppliers = isUserOfTypeSupplier() === false;

    const supplierCompaniesWithActiveCompanyFirst = selectReadableSupplierCompanies(
        ingredientsData.ingredient?.suppliers,
        ingredientsData.readableSupplierIds,
    );

    useEffect(() => {
        if (supplierId === undefined) return;
        setActiveCompanyId(Number(supplierId));
    }, [supplierId]);

    const activePost = ingredientsData.activePost.post;

    const items: IngredientContextInitialValues = {
        ...ingredientsData,
        activePost: activePost,
        isActivePostRefetching: ingredientsData.activePost.isRefetching,
        isActivePostLoading: ingredientsData.activePost.isLoading,
        activePostError: ingredientsData.activePost.error,
        showIngredientSuppliers,
        supplierCompaniesWithActiveCompanyFirst,
        activeCompanyId,
        setActiveCompanyId,
    };

    return (
        <IngredientContext.Provider value={items}>
            <IngredientLoadStateBoundary state={items.ingredientLoadState} onRetry={items.refetchIngredient}>
                {children}
            </IngredientLoadStateBoundary>
        </IngredientContext.Provider>
    );
};

type Options = {
    ingredientId: string | undefined;
    activeCompanyId: number | null;
};
const useAllIngredientData = (options: Options) => {
    const { ingredientId, activeCompanyId } = options;
    const { currentCampaign, selectedCompany, isUserOfTypeSupplier, canUser } = useFoodpilotContext();
    const { company } = useCompanyMetadata();
    const { lastSelectedVariation, setEntitySsq } = useEntityScores();

    const {
        ingredient,
        ingredientLoadState,
        isReady,
        isRefetching,
        refetch: refetchIngredient,
    } = useGetOneIngredient(ingredientId, {
        organization_id: company.id,
    });
    const suppliersLength = ingredient?.suppliers?.length;
    const questionnaireAccess = {
        selectedCompanyId: company.id,
        isSupplierUser: isUserOfTypeSupplier(),
        ingredientOwnerId: ingredient?.ownedBy?.id,
        suppliers: ingredient?.suppliers,
    };
    const answerableSupplierId = getAnswerableSupplierId(questionnaireAccess);
    const readableSupplierIds = getReadableSupplierIds(questionnaireAccess);
    const questionnaireSupplierId = getQuestionnaireSupplierId({
        ...questionnaireAccess,
        preferredSupplierId: activeCompanyId,
    });

    const ingredientTotalScoresParams = useOlapScoresParams({
        scores: {
            variations: ScoreService.getVariationByLastSelectedVariation(lastSelectedVariation),
        },
        prevScores: true,
    });

    const ingredientSuppliersScoresParams = useOlapScoresParams({
        scores: {
            variations: ScoreService.getVariationByLastSelectedVariation(lastSelectedVariation),
            dimensions: [[["suppliers"]]],
        },
    });

    const activePostScoresParams = useOlapScoresParams({
        scores: {
            ids: ScoreService.getScoreIdsByLastSelectedVariation(lastSelectedVariation),
            dimensions: [[["headings"]], [["ssq_versions"], ["headings"]]],
            variations: ScoreService.getVariationByLastSelectedVariation(lastSelectedVariation),
        },
    });

    const { data: scores, isFetching: areScoresRefetching } = useGetIngredientScores({
        ingredientId: Number(ingredientId),
        campaignId: currentCampaign?.id,
        companyId: selectedCompany?.id,
        scoresQuery: ingredientTotalScoresParams.query,
    });

    const { data: suppliersScores, isFetching: areSuppliersScoresRefetching } = useGetIngredientScores({
        ingredientId: Number(ingredientId),
        campaignId: currentCampaign?.id,
        companyId: selectedCompany?.id,
        scoresQuery: ingredientSuppliersScoresParams.query,
        enabled: suppliersLength !== undefined && suppliersLength > 1,
    });

    const ingredientRefCompositeId =
        ingredient?.ingredientReferenceId !== undefined ?
            buildCompositeId(ingredient.referenceSource ?? "sure", ingredient.ingredientReferenceId)
        :   undefined;
    const { ingredientReference, isReady: isIngredientReferenceReady } =
        useGetOneIngredientReferenceUnified(ingredientRefCompositeId);

    const { posts } = useGetIngredientPosts(
        readableSupplierIds.length > 0 ? ingredient?.id : undefined,
        selectedCompany?.id,
        currentCampaign?.id,
        readableSupplierIds.length,
    );
    const canViewSsq =
        canUser(VIEW_SSQ, INGREDIENT) && activeCompanyId !== null && readableSupplierIds.includes(activeCompanyId);
    const activePost = useGetIngredientPost({
        ingredientId: ingredient?.id,
        companyId: company.id,
        supplierId: activeCompanyId,
        campaignId: currentCampaign?.id,
        scoresQuery: activePostScoresParams.query,
        enabled: canViewSsq,
    });

    const { ssqVersion, isReady: isSsqVersionReady } = useGetOneSsqVersion(activePost.post?.ssqVersionId);

    useEffect(() => {
        ingredientTotalScoresParams.state.setQuery({
            ...ingredientTotalScoresParams.query,
            scores: {
                ...ingredientTotalScoresParams.query.scores,
                variations: ScoreService.getVariationsByLastSelectedVariation(
                    ingredientTotalScoresParams.query.scores?.variations,
                    lastSelectedVariation,
                ),
            },
        });

        ingredientSuppliersScoresParams.state.setQuery({
            ...ingredientSuppliersScoresParams.query,
            scores: {
                ...ingredientSuppliersScoresParams.query.scores,
                variations: ScoreService.getVariationByLastSelectedVariation(lastSelectedVariation),
            },
        });

        activePostScoresParams.state.setQuery({
            ...activePostScoresParams.query,
            scores: {
                ...activePostScoresParams.query.scores,
                ids: ScoreService.getScoreIdsByLastSelectedVariation(lastSelectedVariation),
                variations: ScoreService.getVariationByLastSelectedVariation(lastSelectedVariation),
            },
        });
    }, [lastSelectedVariation.scoreId, lastSelectedVariation.variationId]);

    useEffect(() => {
        setEntitySsq(ssqVersion?.ssq);
    }, [ssqVersion]);

    return {
        ingredient,
        ingredientLoadState,
        refetchIngredient,
        isReady,
        isRefetching,
        scores,
        areScoresRefetching,
        suppliersScores,
        areSuppliersScoresRefetching,
        ingredientReference,
        isIngredientReferenceReady,
        ssqVersion,
        isSsqVersionReady,
        posts,
        activePost,
        answerableSupplierId,
        readableSupplierIds,
        questionnaireSupplierId,
        canModifySSQ: activePost.post?.canAnswerSsq ?? false,
        canViewSSQ: activePost.post?.canViewSsq ?? false,
    };
};
